/** The Tour Operators grid search namespaces wired to their suggestion fetchers.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators` on
 * TourOperatorFilterNode (contract-tested). `exact` is deliberately omitted
 * wherever `iexact` exists (parent/company) — the DSL serialises both to `=`,
 * which re-parses to `iexact`; id pinning goes through the backend's all-digits
 * fallback. `id` is exact-only (integer), which round-trips fine.
 *
 * `kind` maps regular/franchise/licensee; `lifecycle` filters the operator's
 * own lifecycle (and, named at all, lists closed tenants too — the list
 * leaves them out otherwise); `events_enabled`/
 * `auto_reply_with_ai`/`restech` filter each operator's OWN stored `config`
 * (no franchise inheritance) — the boolean flags fold their runtime default
 * into the absent-key case (see the backend compiler). `has_app` filters the
 * effective has_app column (a licensee inherits its parent's answer, a sandbox
 * its live operator's).
 */
import type { NamespaceConfigMap, SuggestionFetcher } from '@/components/QuerySearch';

import * as topsApi from '@/api/tourOperators';

export const KIND_LABELS = ['regular', 'franchise', 'licensee'];
/** The tenant lifecycle (tenant retirement); naming it lifts the list's "active tenants only". */
export const LIFECYCLE_LABELS = ['active', 'closed', 'releasing', 'released', 'restoring'];
export const RESTECH_LABELS = ['none', 'fareharbor', 'ventrata', 'bokun', 'rezdy', 'trekksoft', 'xml', 'other'];
const BILLING_PLAN_LABELS = [
  'App usage',
  'App usage or received SMS',
  'App usage or received SMS or WhatsApp',
  'Bokun Marketplace',
];
const YES_NO = ['yes', 'no'];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

/** Suggest operators by code/name via the existing list endpoint. */
function fetchTopSuggestions(kind: 'code' | 'name'): SuggestionFetcher {
  return async (query) => {
    if (!query.trim()) return [];
    const res = await topsApi.list({ search: query, pageSize: 10 });
    return res.results.map((t) => ({
      value: kind === 'code' ? t.code : t.name,
      label: `${t.name} (${t.code})`,
      id: t.id,
    }));
  };
}

export function buildTourOperatorNamespaces(): NamespaceConfigMap {
  return {
    code: {
      label: 'code',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchTopSuggestions('code'),
      help: 'Operator code (unique).',
      examples: ['code=acme', 'code=~acm'],
    },
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchTopSuggestions('name'),
      help: 'Operator name.',
      examples: ['name=~adventures', 'name="Acme Tours"'],
    },
    email: {
      label: 'email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Contact email.',
      examples: ['email=~@acme.com'],
    },
    parent: {
      label: 'parent',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Franchise parent by code; all-digits also matches the parent id; "none" = roots only.',
      examples: ['parent=acmegroup', 'parent=none'],
    },
    kind: {
      label: 'kind',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(KIND_LABELS),
      help: 'regular / franchise / licensee.',
      examples: ['kind=franchise', 'kind=licensee'],
    },
    lifecycle: {
      label: 'lifecycle',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(LIFECYCLE_LABELS),
      help: 'active / closed / releasing / released / restoring (the operator’s own). Naming it lists closed tenants too.',
      examples: ['lifecycle=closed', '!lifecycle=active'],
    },
    billing_plan: {
      label: 'billing_plan',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(BILLING_PLAN_LABELS),
      help: 'Billing plan by label or integer code (0-3).',
      examples: ['billing_plan="Bokun Marketplace"', 'billing_plan=0'],
    },
    has_dashboard_access: {
      label: 'has_dashboard_access',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Staff users have dashboard access (yes/no).',
      examples: ['has_dashboard_access=no'],
    },
    external_payment_processing: {
      label: 'external_payment_processing',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Operator processes payments itself (yes/no).',
      examples: ['external_payment_processing=yes'],
    },
    company: {
      label: 'company',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Accounting company by name; all-digits matches the company id.',
      examples: ['company="Curated Planet"'],
    },
    events_enabled: {
      label: 'events_enabled',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'config.events_enabled (default yes when unset).',
      examples: ['events_enabled=no'],
    },
    has_app: {
      label: 'has_app',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Has a mobile app (the operator’s answer; a licensee inherits its parent’s, a sandbox its live operator’s).',
      examples: ['has_app=no'],
    },
    auto_reply_with_ai: {
      label: 'auto_reply_with_ai',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'config.auto_reply_with_ai (default no when unset).',
      examples: ['auto_reply_with_ai=yes'],
    },
    restech: {
      label: 'restech',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(RESTECH_LABELS),
      help: 'config.onboarding.restech booking integration.',
      examples: ['restech=bokun', 'restech=none'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Tour operator id.',
      examples: ['id=42'],
    },
  };
}
